import React, { Component } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import Button from "../common/Button";
import { mapVehicleToMulkiya } from "./mulkiyaHelpers";
import { MULKIYA_PRINT_CSS } from "./mulkiyaPrintStyles";
import logo from "../../assets/mulkiya-logo.png";
import watermark from "../../assets/mulkiya-wm.png";
import "./mulkiya.css";

const toAbsoluteSrc = (src) => {
  try {
    return new URL(src, window.location.href).href;
  } catch (e) {
    return src;
  }
};

const waitForImages = (doc) =>
  Promise.all(
    Array.from(doc.images || []).map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete) {
            resolve();
            return;
          }
          img.onload = () => resolve();
          img.onerror = () => resolve();
        }),
    ),
  );

export default class MulkiyaModal extends Component {
  componentDidMount() {
    if (this.props.autoPrint) {
      setTimeout(() => this.downloadPdf(), 500);
    }
  }

  downloadPdf = async () => {
    const printArea = document.getElementById("mkPrintArea");
    if (!printArea) return;

    const clone = printArea.cloneNode(true);
    clone.querySelectorAll("img").forEach((img) => {
      const src = img.getAttribute("src") || img.src;
      img.setAttribute("src", toAbsoluteSrc(src));
      img.removeAttribute("srcset");
    });

    const printWindow = window.open("", "_blank", "width=900,height=1000");
    if (!printWindow) {
      window.alert("Please allow popups to download the PDF");
      return;
    }

    printWindow.document.open();
    printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <base href="${window.location.origin}/" />
  <title>Mulkiya - Registration Card</title>
  <style>${MULKIYA_PRINT_CSS}</style>
</head>
<body>
  ${clone.outerHTML}
</body>
</html>`);
    printWindow.document.close();

    await waitForImages(printWindow.document);
    // small delay so fonts/gradients paint
    await new Promise((r) => setTimeout(r, 400));

    try {
      printWindow.focus();
      printWindow.print();
    } finally {
      setTimeout(() => {
        try {
          printWindow.close();
        } catch (e) {
          // ignore
        }
      }, 800);
    }
  };

  render() {
    const { vehicle, onClose, translate } = this.props;
    if (!vehicle) return null;
    const f = mapVehicleToMulkiya(vehicle);
    const [statusClass, statusText] = f._status || ["na", ""];

    return (
      <Dialog
        open={!!vehicle}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        aria-labelledby="mulkiya-dialog-title"
        PaperProps={{
          sx: {
            borderRadius: "18px",
            maxHeight: "calc(100vh - 48px)",
          },
        }}
      >
        <DialogTitle
          id="mulkiya-dialog-title"
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
            pr: 1,
            pb: 1,
          }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#1a1d4f" }}>
              {translate?.("registrationCard") || "Registration Card"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {f._sub}
            </Typography>
          </Box>
          <IconButton aria-label="close" onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          <div className="mk-doc" id="mkPrintArea">
            <div className="mk-doc-title">
              <div className="ar">ملكية مركبة</div>
              <div className="en">Registration Card</div>
            </div>

            <section className="mk-card" aria-label="Registration card front">
              <img className="wm" src={watermark} alt="" />
              <div className="c-head">
                <div className="en">
                  BAYANAT FLEET
                  <small>Vehicle Registration</small>
                </div>
                <img src={logo} alt="Bayanat" />
                <div className="ar">
                  بيانات أسطول المركبات
                  <small>تسجيل المركبة</small>
                </div>
              </div>
              <div className="c-tbl">
                <div className="r">
                  <div className="l">Traffic Plate No.</div>
                  <div className="v">
                    <span>{f.plate}</span>
                    <span className="ar">{f.plateArabic}</span>
                  </div>
                  <div className="la ar">رقم اللوحة</div>
                </div>
                <div className="r">
                  <div className="l">Place of Issue</div>
                  <div className="v">
                    <span>{f.cityEn}</span>
                    <span className="ar">{f.cityAr}</span>
                  </div>
                  <div className="la ar">جهة الترخيص</div>
                </div>
                <div className="r pair">
                  <div className="l">Contract No.</div>
                  <div className="v mono">{f.contractNumber}</div>
                  <div className="m">
                    <span className="ar">رقم العقد</span>
                    <span className="en">Plate Class</span>
                  </div>
                  <div className="v">
                    <span>{f.plateCategory}</span>
                    <span className="ar">{f.plateCategoryAr}</span>
                  </div>
                  <div className="la ar">صنف اللوحة</div>
                </div>
                <div className="r">
                  <div className="l">Owner</div>
                  <div className="v">{f.owner}</div>
                  <div className="la ar">المالك</div>
                </div>
                <div className="r">
                  <div className="l">Vehicle ID</div>
                  <div className="v mono">{f.id}</div>
                  <div className="la ar">رقم المركبة</div>
                </div>
                <div className="r pair">
                  <div className="l">Exp. Date</div>
                  <div className="v">{f.expDate}</div>
                  <div className="m">
                    <span className="ar">انتهاء الترخيص</span>
                    <span className="en">Reg. Date</span>
                  </div>
                  <div className="v">{f.regDate}</div>
                  <div className="la ar">تاريخ التسجيل</div>
                </div>
                <div className="r pair">
                  <div className="l">Ins. Exp.</div>
                  <div className="v">{f.insExp}</div>
                  <div className="m">
                    <span className="ar">انتهاء التأمين</span>
                    <span className="en">On Lease</span>
                  </div>
                  <div className="v">{f.lease}</div>
                  <div className="la ar">مؤجرة</div>
                </div>
                <div className="r">
                  <div className="l">Remarks</div>
                  <div className="v">
                    <span className={`status ${statusClass}`}>{statusText}</span>
                  </div>
                  <div className="la ar">ملاحظات</div>
                </div>
              </div>
              <div className="c-foot">
                <div className="note">
                  Generated from Bayanat fleet records — not an official
                  government document
                  <br />
                  <span className="ar">
                    صادرة من سجلات بيانات — ليست وثيقة حكومية رسمية
                  </span>
                </div>
                <div className="stamp">BAYANAT</div>
              </div>
            </section>

            <section className="mk-card" aria-label="Registration card back">
              <img className="wm" src={watermark} alt="" />
              <div className="c-sec">
                <span className="en">Vehicle Information</span>
                <span className="ar">بيانات المركبة</span>
              </div>
              <div className="c-tbl">
                <div className="r pair">
                  <div className="l">Model</div>
                  <div className="v">{f.year}</div>
                  <div className="m">
                    <span className="ar">سنة الصنع</span>
                    <span className="en">Variant</span>
                  </div>
                  <div className="v">{f.variant}</div>
                  <div className="la ar">الفئة</div>
                </div>
                <div className="r">
                  <div className="l">Origin</div>
                  <div className="v">{f.origin}</div>
                  <div className="la ar">بلد الصنع</div>
                </div>
                <div className="r pair">
                  <div className="l">Color</div>
                  <div className="v">{f.color}</div>
                  <div className="m">
                    <span className="ar">اللون</span>
                    <span className="en">Fuel Type</span>
                  </div>
                  <div className="v">{f.fuel}</div>
                  <div className="la ar">نوع الوقود</div>
                </div>
                <div className="r">
                  <div className="l">Veh. Type</div>
                  <div className="v">{f.vehType}</div>
                  <div className="la ar">نوع المركبة</div>
                </div>
                <div className="r pair">
                  <div className="l">Transmission</div>
                  <div className="v">{f.transmission}</div>
                  <div className="m">
                    <span className="ar">ناقل الحركة</span>
                    <span className="en">Capacity</span>
                  </div>
                  <div className="v">{f.capacity}</div>
                  <div className="la ar">السعة</div>
                </div>
                <div className="r pair">
                  <div className="l">Odometer</div>
                  <div className="v">{f.odometer}</div>
                  <div className="m">
                    <span className="ar">عداد المسافات</span>
                    <span className="en">Fuel Eff.</span>
                  </div>
                  <div className="v">{f.efficiency}</div>
                  <div className="la ar">كفاءة الوقود</div>
                </div>
                <div className="r">
                  <div className="l">Chassis No.</div>
                  <div className="v mono">{f.vin}</div>
                  <div className="la ar">رقم القاعدة</div>
                </div>
                <div className="r">
                  <div className="l">Device IMEI</div>
                  <div className="v mono">{f.imei}</div>
                  <div className="la ar">رقم الجهاز</div>
                </div>
              </div>
              <div className="c-foot">
                <div>
                  <div className="brand">bayanatss</div>
                  <div className="note">Fleet management system</div>
                </div>
                <div className="stamp">BAYANAT</div>
              </div>
            </section>

            <div className="mk-src">
              Generated through Bayanat Smart Systems
            </div>
          </div>
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button onClick={onClose}>
            {translate?.("sharedCancel") || "Cancel"}
          </Button>
          <Button onClick={this.downloadPdf}>
            {translate?.("downloadPDF") || "Download PDF"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}
